/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';

import {ApiConfig} from '../config/api-config';
import {AdnApiClientService} from '../common/controller/adn-api-client.service';
import {SignalAdminResourcePage} from '../common/controller/signal-admin-resource-api';
import {MoneyValue} from '../../components/util/money';

const EXCEL_MEDIA_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export interface PoliticalSponsorSummaryParams {
  network?: string;
  groupBy: string;
  page: number;
  pageSize: number;
}

export interface PoliticalSponsorBudgetComparisonParams {
  page: number;
  pageSize: number;
}

export interface PoliticalSponsorBudgetTopic {
  name: string;
  budget: MoneyValue;
}

export interface PoliticalSponsorElectionBudget {
  election: string | null;
  electionName: string | null;
  electionDate: string | null;
  totalBudget: MoneyValue | null;
  topics: PoliticalSponsorBudgetTopic[];
}

export interface PoliticalSponsorBudgetAdvertiser {
  id: string;
  network: string;
  name: string;
  legalName: string | null;
  vatId: string | null;
  createTime: string;
  budgets: PoliticalSponsorElectionBudget[];
  comparison: 'BASELINE' | 'MATCHES' | 'DIFFERS';
}

export interface PoliticalSponsorBudgetGroup {
  advertiserCount: number;
  advertisers: PoliticalSponsorBudgetAdvertiser[];
}

@Injectable({providedIn: 'root'})
export class PoliticalSponsorApiService {
  constructor(private readonly client: AdnApiClientService, private readonly apiConfig: ApiConfig) {
  }

  summary(params: PoliticalSponsorSummaryParams): Observable<any> {
    return this.client.request({
      method: 'GET',
      url: this.apiConfig.getVersionedUri('politicalsponsors'),
      params
    });
  }

  budgetComparisons(params: PoliticalSponsorBudgetComparisonParams): Observable<SignalAdminResourcePage<PoliticalSponsorBudgetGroup>> {
    return this.client.request<SignalAdminResourcePage<PoliticalSponsorBudgetGroup>>({
      method: 'GET',
      url: this.apiConfig.getVersionedUri('politicalsponsors/budgetcomparisons'),
      params
    });
  }

  exportAdvertisers(): Observable<Blob> {
    return this.client.request<Blob>({
      method: 'GET',
      url: this.apiConfig.getVersionedUri('politicalsponsors/export'),
      headers: {Accept: EXCEL_MEDIA_TYPE},
      responseType: 'blob'
    });
  }
}
